import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import {
  Download, FileText, Shield, Check, Scale, FileCheck, AlertCircle,
  Users, Mail, MapPin, Clock,
} from 'lucide-react';

// TermsOfService — Swiss Minimalist + Pastel theme.
// Pastels: sage #D4E0DA · peach #EBD8C8 · lavender #D9CFD9
// Fonts: .font-display · .font-body · .font-label

const TermsOfService = () => {
  const navigate = useNavigate();

  const highlights = [
    { tint: '#D4E0DA', title: 'Service availability',     description: 'We strive for 99.9% uptime with scheduled maintenance windows.' },
    { tint: '#EBD8C8', title: 'Your responsibilities',    description: 'Maintain account security and provide accurate information.' },
    { tint: '#D9CFD9', title: 'Payment terms',            description: 'Clear pricing with defined payment schedules and refund policies.' },
    { tint: '#D4E0DA', title: 'Intellectual property',    description: 'You retain ownership of your data; we retain our platform rights.' },
    { tint: '#EBD8C8', title: 'Termination rights',       description: 'Either party may terminate with proper notice per agreement.' },
    { tint: '#D9CFD9', title: 'Limitation of liability',  description: 'Defined liability caps and indemnification provisions.' },
  ];

  const coreTopics = [
    { icon: Scale,     label: 'Acceptance and scope of Terms' },
    { icon: Users,     label: 'Account creation and user responsibilities' },
    { icon: Shield,    label: 'Service description and availability' },
    { icon: FileCheck, label: 'Intellectual property rights' },
  ];

  const moreTopics = [
    'Payment terms and billing practices',
    'Warranties and disclaimers',
    'Limitation of liability',
    'Indemnification obligations',
    'Termination and suspension rights',
    'Dispute resolution and governing law',
  ];

  const importantSections = [
    { icon: Users,       tint: '#D4E0DA', title: 'Account & access',        description: 'You are responsible for maintaining the confidentiality of your account credentials and all activities under your account.' },
    { icon: FileCheck,   tint: '#EBD8C8', title: 'Acceptable use',          description: 'You agree not to misuse our Services, interfere with operations, or violate any applicable laws or regulations.' },
    { icon: Shield,      tint: '#D9CFD9', title: 'Service modifications',   description: 'We may modify or discontinue Services with reasonable notice, except for critical security updates.' },
    { icon: AlertCircle, tint: '#D4E0DA', title: 'Disclaimer of warranties', description: 'Services are provided "as is" with no warranties except as expressly stated in our agreements.' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] font-body">
      <Header />

      {/* ===================== HERO ===================== */}
      <section
        className="bg-[#D4E0DA]/55 dark:bg-[#101F18]/55 pt-24 md:pt-32 pb-24 md:pb-32 px-6 border-b border-black/10 dark:border-white/10 min-h-[480px] md:min-h-[640px]"
        data-testid="terms-hero"
      >
        <div className="max-w-5xl mx-auto text-center w-full">
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-black/55 dark:text-white/55 mb-8">
            Legal
          </p>
          <h1 className="font-display font-semibold leading-[1.25] tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-8">
            Terms of
            <br />
            <span className="italic text-black/85 dark:text-white/90">Service.</span>
          </h1>
          <p className="font-body text-base md:text-lg text-black/70 dark:text-white/70 max-w-3xl mx-auto leading-relaxed mb-10">
            The legal agreement governing your use of Nova's AI-powered audit‑readiness platform.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => window.open('/ethira-terms-of-service.pdf', '_blank')}
              data-testid="terms-download-hero"
              className="font-label bg-[#0A0A0A] dark:bg-[#141414] text-[#FAFAFA] px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] hover:bg-[#EBD8C8] dark:hover:bg-[#2E1B10] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] transition-all duration-500 inline-flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
              Download Full Terms
            </button>
            <button
              type="button"
              onClick={() => navigate('/contact')}
              data-testid="terms-contact"
              className="font-label border border-[#0A0A0A]/20 dark:border-white/25 text-[#0A0A0A] dark:text-[#F5F5F5] px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] hover:border-[#0A0A0A] dark:hover:border-white/30 hover:bg-[#D9CFD9]/40 dark:hover:bg-[#1E1724]/40 transition-all duration-500"
            >
              Contact Legal Team
            </button>
          </div>
        </div>
      </section>

      {/* ===================== OVERVIEW + DOC INFO ===================== */}
      <section className="px-6 py-24 md:py-28" data-testid="terms-overview">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-16 items-start">
          <div>
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-4">
              Overview
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-8 leading-tight">
              The agreement, <span className="italic text-black/85 dark:text-white/90">explained.</span>
            </h2>
            <div className="space-y-5 text-base md:text-lg text-black/75 dark:text-white/75 leading-relaxed">
              <p>
                These Terms of Service ("Terms") constitute a legal agreement between you (the "Customer" or "you") and <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">Inceptica LLC</strong>, doing business as <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">Nova</strong> ("Nova," "we," "us," or "our"). By accessing or using our AI-powered compliance and certification platform, AI assessment tools, and related services (collectively, the "Services"), you agree to be bound by these Terms.
              </p>
              <p>
                Please read these Terms carefully before using our Services. If you do not agree with any part of these Terms, you must not use our Services.
              </p>
            </div>
          </div>

          <div className="rounded-2xl bg-[#EBD8C8]/45 dark:bg-[#2E1B10]/45 p-8 md:p-10">
            <div className="flex items-start gap-3 mb-5">
              <FileText className="w-5 h-5 mt-0.5 text-black/65 dark:text-white/65" strokeWidth={1.5} />
              <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55">
                Document Information
              </p>
            </div>
            <div className="space-y-5">
              <div>
                <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Effective Date</p>
                <p className="font-display text-lg md:text-xl leading-tight">November 28, 2025</p>
              </div>
              <div className="border-t border-black/10 dark:border-white/10 pt-5">
                <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Last Updated</p>
                <p className="font-display text-lg md:text-xl leading-tight">November 2025</p>
              </div>
              <div className="border-t border-black/10 dark:border-white/10 pt-5">
                <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Governing Law</p>
                <p className="font-display text-lg md:text-xl leading-tight">State of Connecticut, USA</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== KEY HIGHLIGHTS (BENTO) ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="terms-highlights">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
              Key Terms Highlights
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-4">
              The essentials at a <span className="italic text-black/85 dark:text-white/90">glance.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-black/10 dark:border-white/10">
            {highlights.map((h, i) => (
              <div
                key={h.title}
                data-testid={`terms-highlight-${i}`}
                className="group relative border-r border-b border-black/10 dark:border-white/10 p-8 md:p-10 transition-colors duration-700 flex flex-col"
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = h.tint + '40'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-8 nv-tint"
                  style={{ backgroundColor: h.tint + 'CC' }}
                >
                  <Scale className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                </div>
                <p
                  className="font-display font-semibold leading-none text-5xl md:text-6xl mb-5 select-none"
                  style={{ color: h.tint }}
                >
                  {String(i + 1).padStart(2, '0')}
                </p>
                <h3 className="font-display text-lg md:text-xl leading-tight mb-3">{h.title}</h3>
                <p className="font-body text-sm md:text-base text-black/65 dark:text-white/65 leading-relaxed">{h.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== WHAT'S COVERED ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="terms-covered">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
              What's Covered
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-4 leading-tight">
              Across the document.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <div className="rounded-2xl border border-black/10 dark:border-white/10 p-8 md:p-10 bg-[#FAFAFA] dark:bg-[#050505]">
              <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-5">
                Core Topics
              </p>
              <ul className="space-y-4">
                {coreTopics.map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-start gap-3">
                    <Icon className="w-4 h-4 mt-1 shrink-0 text-black/55 dark:text-white/55" strokeWidth={1.5} />
                    <span className="font-body text-base text-black/75 dark:text-white/75 leading-relaxed">{label}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-black/10 dark:border-white/10 p-8 md:p-10 bg-[#FAFAFA] dark:bg-[#050505]">
              <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-5">
                Additional Details
              </p>
              <ul className="space-y-3">
                {moreTopics.map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <Check className="w-4 h-4 mt-1 shrink-0 text-black/55 dark:text-white/55" strokeWidth={1.5} />
                    <span className="font-body text-sm md:text-base text-black/75 dark:text-white/75 leading-relaxed">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== IMPORTANT SECTIONS ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="terms-important">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
              Important Sections
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-4 leading-tight">
              Worth reading <span className="italic text-black/85 dark:text-white/90">carefully.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {importantSections.map(({ icon: Icon, tint, title, description }, i) => (
              <div
                key={title}
                data-testid={`terms-important-${i}`}
                className="rounded-2xl border border-black/10 dark:border-white/10 p-8 md:p-10 hover:border-black/20 dark:hover:border-white/20 transition-colors duration-500 flex items-start gap-5"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 nv-tint"
                  style={{ backgroundColor: tint + 'CC' }}
                >
                  <Icon className="w-6 h-6 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-display text-xl md:text-2xl leading-tight mb-3">{title}</h3>
                  <p className="font-body text-sm md:text-base text-black/65 dark:text-white/65 leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CONTACT ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="terms-contact-section">
        <div className="max-w-5xl mx-auto rounded-2xl bg-[#D9CFD9]/45 dark:bg-[#1E1724]/45 p-8 md:p-12">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
            <div>
              <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
                Questions about our Terms?
              </p>
              <h3 className="font-display font-semibold text-2xl md:text-4xl tracking-tight leading-tight">
                Talk to our Legal Team.
              </h3>
            </div>
            <ul className="space-y-5">
              <li className="flex items-start gap-4">
                <Mail className="w-4 h-4 mt-1.5 shrink-0 text-black/65 dark:text-white/65" strokeWidth={1.5} />
                <div>
                  <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Email</p>
                  <a href="mailto:legal@teamnova.ai" className="font-display text-lg md:text-xl leading-tight underline-offset-4 hover:underline">legal@teamnova.ai</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <MapPin className="w-4 h-4 mt-1.5 shrink-0 text-black/65 dark:text-white/65" strokeWidth={1.5} />
                <div>
                  <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Address</p>
                  <p className="font-body text-base text-black/80 dark:text-white/85 leading-relaxed">2389 Main St. STE 100, Glastonbury, CT 06033</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Clock className="w-4 h-4 mt-1.5 shrink-0 text-black/65 dark:text-white/65" strokeWidth={1.5} />
                <div>
                  <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Response Time</p>
                  <p className="font-body text-base text-black/80 dark:text-white/85 leading-relaxed">Within 48 hours · 24 hours for urgent legal matters</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA (INVERTED) ===================== */}
      <section className="bg-[#0A0A0A] dark:bg-[#141414] py-24 md:py-32 px-6 text-center" data-testid="terms-cta-section">
        <div className="max-w-4xl mx-auto">
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-white/65 mb-6">
            Download
          </p>
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-[#FAFAFA] mb-6 leading-[1.1] tracking-tight">
            Read the full Terms.
          </h2>
          <p className="font-body text-sm md:text-base text-white/70 mb-12 max-w-2xl mx-auto">
            Access the complete legal document with all conditions and provisions governing your use of Nova's services.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => window.open('/ethira-terms-of-service.pdf', '_blank')}
              data-testid="terms-cta-download"
              className="font-label bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] hover:bg-[#D4E0DA] dark:hover:bg-[#101F18] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full transition-all duration-500 inline-flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
              Download Terms (PDF)
            </button>
            <button
              type="button"
              onClick={() => navigate('/privacy-policy')}
              data-testid="terms-cta-privacy"
              className="font-label border border-white/25 bg-transparent text-[#FAFAFA] hover:bg-[#FAFAFA] dark:hover:bg-[#050505] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full transition-all duration-500"
            >
              View Privacy Policy
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfService;
